import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { eventsAPI, formatApiErrorDetail } from '../../lib/api';
import { Plus, CalendarBlank, MapPin, Clock, Eye, EyeSlash, PencilSimple, Trash, Globe } from '@phosphor-icons/react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

const EVENT_TYPES = ['general', 'investiture', 'ceremony', 'meeting', 'chapter', 'social', 'charitable'];

const AdminEventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '', description: '', event_type: 'general', date: '',
    time: '', location: '', is_public: false
  });

  const fetchData = async () => {
    try {
      const response = await eventsAPI.getAll();
      setEvents(response.data.sort((a, b) => new Date(b.date) - new Date(a.date)));
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await eventsAPI.update(editingEvent.id, formData);
        toast.success('Event updated');
      } else {
        await eventsAPI.create(formData);
        toast.success('Event created');
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title || '',
      description: event.description || '',
      event_type: event.event_type || 'general',
      date: event.date || '',
      time: event.time || '',
      location: event.location || '',
      is_public: event.is_public || false
    });
    setShowModal(true);
  };

  const handleDelete = async (event) => {
    if (!window.confirm(`Are you sure you want to delete "${event.title}"?`)) return;
    try {
      await eventsAPI.delete(event.id);
      toast.success('Event deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleTogglePublic = async (event) => {
    try {
      await eventsAPI.update(event.id, { ...event, is_public: !event.is_public });
      toast.success(event.is_public ? 'Event hidden from website' : 'Event published to website');
      fetchData();
    } catch (error) {
      toast.error('Failed to update event');
    }
  };

  const resetForm = () => {
    setEditingEvent(null);
    setFormData({ title: '', description: '', event_type: 'general', date: '', time: '', location: '', is_public: false });
  };

  const upcomingEvents = events.filter(e => new Date(e.date) >= new Date());
  const pastEvents = events.filter(e => new Date(e.date) < new Date());

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-events-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl text-[#1A1614]">Events</h1>
            <p className="text-[#8A847A]">{events.length} total events • {events.filter(e => e.is_public).length} public</p>
          </div>
          <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-[#7B1818] hover:bg-[#5C1212] rounded-none" data-testid="add-event-button">
            <Plus size={18} className="mr-2" /> New Event
          </Button>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 p-4 flex items-start gap-3">
          <Globe size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-900 font-medium">Website Publishing</p>
            <p className="text-sm text-blue-700">
              Events marked as "Public" will appear on the public Events page of the website. 
              Use the toggle to control which events are visible to the public.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : (
          <div className="space-y-8">
            {/* Upcoming Events */}
            <div>
              <h2 className="font-heading text-xl text-[#1A1614] mb-4">Upcoming Events</h2>
              {upcomingEvents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {upcomingEvents.map(event => (
                    <div key={event.id} className="bg-white border border-[#D8D2C4] p-6" data-testid={`event-card-${event.id}`}>
                      <div className="flex gap-4">
                        <div className="w-16 h-16 bg-[#7B1818] text-white flex flex-col items-center justify-center flex-shrink-0">
                          <span className="text-xl font-heading">{new Date(event.date).getDate()}</span>
                          <span className="text-xs">{new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}</span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-overline text-xs">{event.event_type?.toUpperCase()}</span>
                            {event.is_public ? (
                              <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 flex items-center gap-1">
                                <Globe size={12} /> On Website
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600">Internal Only</span>
                            )}
                          </div>
                          <h3 className="font-heading text-lg text-[#1A1614]">{event.title}</h3>
                          <div className="flex flex-wrap gap-4 mt-2 text-sm text-[#8A847A]">
                            {event.time && <span className="flex items-center gap-1"><Clock size={14} />{event.time}</span>}
                            {event.location && <span className="flex items-center gap-1"><MapPin size={14} />{event.location}</span>}
                          </div>
                        </div>
                      </div>
                      {/* Actions */}
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#D8D2C4]">
                        <button
                          onClick={() => handleTogglePublic(event)}
                          className={`flex items-center gap-2 px-3 py-1.5 text-sm transition-colors ${
                            event.is_public 
                              ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          data-testid={`toggle-public-${event.id}`}
                        >
                          {event.is_public ? <Eye size={16} /> : <EyeSlash size={16} />}
                          {event.is_public ? 'Published' : 'Publish to Website'}
                        </button>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(event)}
                            className="p-2 hover:bg-[#F5F2EB] text-[#4A453E]"
                            data-testid={`edit-event-${event.id}`}
                          >
                            <PencilSimple size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(event)}
                            className="p-2 hover:bg-red-50 text-red-600"
                            data-testid={`delete-event-${event.id}`}
                          >
                            <Trash size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#8A847A] bg-white border border-[#D8D2C4] p-8 text-center">No upcoming events</p>
              )}
            </div>

            {/* Past Events */}
            {pastEvents.length > 0 && (
              <div>
                <h2 className="font-heading text-xl text-[#1A1614] mb-4">Past Events</h2>
                <div className="bg-white border border-[#D8D2C4]">
                  <table className="w-full data-table">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 text-left">Event</th>
                        <th className="px-4 py-3 text-left">Type</th>
                        <th className="px-4 py-3 text-left">Date</th>
                        <th className="px-4 py-3 text-left">Location</th>
                        <th className="px-4 py-3 text-center">Website</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pastEvents.slice(0, 10).map(event => (
                        <tr key={event.id}>
                          <td className="px-4 py-3 text-[#1A1614]">{event.title}</td>
                          <td className="px-4 py-3 text-[#4A453E] capitalize">{event.event_type}</td>
                          <td className="px-4 py-3 text-[#4A453E]">{new Date(event.date).toLocaleDateString()}</td>
                          <td className="px-4 py-3 text-[#4A453E]">{event.location || '-'}</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleTogglePublic(event)}
                              className={`px-2 py-1 text-xs ${
                                event.is_public 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-gray-100 text-gray-500'
                              }`}
                            >
                              {event.is_public ? 'Public' : 'Hidden'}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => handleEdit(event)}
                              className="p-1 hover:bg-[#F5F2EB] text-[#4A453E]"
                            >
                              <PencilSimple size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(event)}
                              className="p-1 hover:bg-red-50 text-red-600 ml-1"
                            >
                              <Trash size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">{editingEvent ? 'Edit Event' : 'New Event'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="event-form">
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required className="rounded-none" data-testid="event-input-title" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Event Type</label>
                  <Select value={formData.event_type} onValueChange={(v) => setFormData({...formData, event_type: v})}>
                    <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date *</label>
                  <Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} required className="rounded-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Time</label>
                  <Input type="time" value={formData.time} onChange={(e) => setFormData({...formData, time: e.target.value})} className="rounded-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Location</label>
                  <Input value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} className="rounded-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} className="rounded-none" />
              </div>
              
              {/* Publish to Website Toggle */}
              <div className="bg-[#F5F2EB] p-4 border border-[#D8D2C4]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Globe size={20} className="text-[#7B1818]" />
                    <div>
                      <p className="font-medium text-[#1A1614]">Publish to Website</p>
                      <p className="text-sm text-[#8A847A]">Show this event on the public Events page</p>
                    </div>
                  </div>
                  <Switch 
                    checked={formData.is_public} 
                    onCheckedChange={(v) => setFormData({...formData, is_public: v})} 
                    data-testid="event-toggle-public"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="rounded-none">Cancel</Button>
                <Button type="submit" className="bg-[#7B1818] hover:bg-[#5C1212] rounded-none" data-testid="event-submit-button">
                  {editingEvent ? 'Update Event' : 'Create Event'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminEventsPage;
